/**
 * bw-configurator-events.js
 * Additive GA4 instrumentation for configurator funnels.
 *
 * Does not intercept or replace cart adds. window.fetch is wrapped only to
 * observe successful POSTs to /cart/add.js: the original promise is returned
 * immediately, the original Response is never consumed, and reporting reads
 * a clone after the network call has already completed.
 *
 * Events: configurator_start, configuration_priced, configurator_complete, bw_add_to_cart
 * bw_add_to_cart: theme-side add with builder detail. GA4's standard add_to_cart
 * (with items) comes from Shopify's Google & YouTube pixel, so it is not repeated here.
 * configurator_start: the first trusted dimension / colour action in the older
 * builders, or the first trusted choice inside any other [data-bw-builder] root.
 * configurator_complete: once per page load, when a started configurator reaches
 * its review step (called by the builder).
 * configuration_priced: once per page load, on the first trusted colour or
 * dimension action after a colour is selected AND both dimensions of any one
 * item are filled. data-bw-priced="colour" and data-bw-priced="dim" count as
 * those hits. A builder that marks dimensions and no colour controls fires
 * when those dimension fields are filled. pricedFired is set before gtag
 * runs. Not fired from variant resolution.
 * Destination: window.gtag (GA4 G-9X115J0X65)
 */
(function () {
  'use strict';

  var HELPER_TITLES = [
    'cutting service',
    'edging service',
    'cnc machining',
    'cnc machining - arris edge'
  ];
  var DETAIL_KEYS = [
    'variant_id',
    'price_key',
    'brand',
    'panel_count',
    'band_code',
    'band_gbp',
    'price_model',
    'items_count_raw',
    'items_count_filtered',
    'helpers_excluded'
  ];

  var started = false;
  var pricedFired = false;
  var completed = false;

  function fireGtag(name, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
    } catch (e) {}
  }

  function pagePath() {
    try {
      return String(window.location.pathname || '');
    } catch (e) {
      return '';
    }
  }

  function toMoney(n) {
    var x = Number(n);
    if (!isFinite(x)) return 0;
    return Math.round(x * 100) / 100;
  }

  function detectBuilder(explicit) {
    if (explicit) return String(explicit);
    try {
      var attrEl = document.querySelector('[data-bw-builder]');
      if (attrEl) {
        var fromAttr = attrEl.getAttribute('data-bw-builder');
        if (fromAttr) return String(fromAttr);
      }
    } catch (e) {}
    return 'unknown';
  }

  function buildParams(detail) {
    var src = detail || {};
    var params = {
      builder: detectBuilder(src.builder),
      page_path: src.page_path || pagePath(),
      currency: 'GBP'
    };
    if (src.value != null && src.value !== '') params.value = toMoney(src.value);
    for (var i = 0; i < DETAIL_KEYS.length; i++) {
      var key = DETAIL_KEYS[i];
      if (src[key] != null && src[key] !== '') params[key] = src[key];
    }
    return params;
  }

  function start(detail) {
    try {
      if (started) return;
      started = true;
      fireGtag('configurator_start', buildParams(detail));
    } catch (e) {}
  }

  function priced(detail) {
    // Intentionally a no-op. configuration_priced is no longer tied to
    // variant resolution or recalculation. Existing per-builder calls stay
    // in place so they can be reverted with this file alone; they must not
    // emit. The event is fired from trusted document listeners below.
  }

  function added(detail) {
    try {
      fireGtag('bw_add_to_cart', buildParams(detail));
    } catch (e) {}
  }

  function complete(detail) {
    try {
      if (completed || !started) return;
      completed = true;
      fireGtag('configurator_complete', buildParams(detail));
    } catch (e) {}
  }

  window.BWConfigEvents = {
    start: start,
    priced: priced,
    added: added,
    complete: complete,
    detectBuilder: detectBuilder
  };

  function inBuilder(el) {
    try {
      return !!(el && el.closest && el.closest('[data-panel-builder], [data-bw-wt], .bw-wt, .panel-builder'));
    } catch (e) {
      return false;
    }
  }

  function isDimensionField(el) {
    try {
      return !!(el && el.closest && el.closest('.pb-l, .pb-w, [data-bw-w], [data-bw-d]'));
    } catch (e) {
      return false;
    }
  }

  function isColourOrMaterialTarget(el) {
    try {
      if (!el || !el.closest) return false;
      if (el.closest('.pb-swatch--more, [data-pb-expand-colours], [data-pb-showall]')) return false;
      return !!(el.closest('.pb-swatch, .bw-wt__swatch, [data-colour-handle], [data-pb-tab]'));
    } catch (e) {
      return false;
    }
  }

  function onMaybeStart(ev) {
    try {
      if (started) return;
      if (!ev || !ev.isTrusted) return;
      var t = ev.target;
      if (!inBuilder(t)) {
        // Builders tagged only with data-bw-builder (cubicles, IPS, vanity, EasyCab, EGGER, ...):
        // the first trusted choice inside them starts the configurator.
        var root = builderRoot(t);
        if (!root) return;
        if (ev.type === 'click' && !(t.closest && t.closest('button, a, input, label, select, [role="radio"], [role="button"]'))) return;
        start({ builder: root.getAttribute('data-bw-builder') });
        return;
      }
      if (ev.type === 'input' || ev.type === 'change') {
        if (!isDimensionField(t)) return;
        var field = t.closest('.pb-l, .pb-w, [data-bw-w], [data-bw-d]');
        var val = field ? String(field.value || '').trim() : '';
        if (!val) return;
        start({});
        return;
      }
      if (ev.type === 'click' && (isColourOrMaterialTarget(t) || pricedRole(t) === 'colour' || pricedRole(t) === 'dim')) start({});
    } catch (e) {}
  }

  try {
    document.addEventListener('input', onMaybeStart, true);
    document.addEventListener('change', onMaybeStart, true);
    document.addEventListener('click', onMaybeStart, true);
  } catch (e) {}

  function builderRoot(el) {
    try {
      var found = el && el.closest ? el.closest('[data-bw-builder]') : null;
      if (found) return found;
      // A priced control can be replaced in the same click (cabinet size cards
      // redraw after writing the select). The node is then detached, so the
      // deferred check uses the only builder on the page.
      if (el && el.isConnected === false) {
        var all = document.querySelectorAll('[data-bw-builder]');
        if (all.length === 1) return all[0];
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function fieldFilled(el) {
    if (!el) return false;
    var v = String(el.value || '').trim();
    if (!v) return false;
    var n = Number(v);
    if (isFinite(n)) return n > 0;
    return true;
  }

  function pricedMark(el) {
    try {
      return el && el.closest ? el.closest('[data-bw-priced]') : null;
    } catch (e) {
      return null;
    }
  }

  function pricedRole(el) {
    var node = pricedMark(el);
    if (!node) return '';
    return String(node.getAttribute('data-bw-priced') || '');
  }

  function isPricedDimField(el) {
    if (!el || el.nodeType !== 1) return false;
    var tag = el.tagName;
    if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') return false;
    if (el.disabled || el.hidden || el.type === 'hidden') return false;
    return true;
  }

  function fieldShown(el) {
    try {
      return !!(el && el.getClientRects && el.getClientRects().length);
    } catch (e) {
      return false;
    }
  }

  function pricedDimsComplete(root, clickedEl) {
    var nodes;
    var marked = clickedEl && pricedMark(clickedEl);
    var buttonHit = !!(marked && marked.tagName === 'BUTTON' && pricedRole(clickedEl) === 'dim');
    try {
      nodes = root.querySelectorAll('[data-bw-priced="dim"]');
    } catch (e) {
      return false;
    }
    var seen = false;
    for (var i = 0; i < nodes.length; i++) {
      if (!isPricedDimField(nodes[i])) continue;
      // A phone journey hides the desktop selects. A tap on the phone's own
      // size control still completes the price; a visible empty field does not.
      if (buttonHit && !fieldShown(nodes[i])) continue;
      seen = true;
      if (!fieldFilled(nodes[i])) return false;
    }
    if (seen) return true;
    return buttonHit;
  }

  function dimButtonPending(root, clickedEl) {
    var buttons = false;
    try {
      var nodes = root.querySelectorAll('[data-bw-priced="dim"]');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].tagName === 'BUTTON') { buttons = true; break; }
      }
    } catch (e) {
      return false;
    }
    if (!buttons) return false;
    var marked = clickedEl && pricedMark(clickedEl);
    if (marked && marked.tagName === 'BUTTON' && pricedRole(clickedEl) === 'dim') return false;
    try {
      if (root.querySelector('[data-bw-priced="dim"].is-on, [data-bw-priced="dim"].is-active, [data-bw-priced="dim"].is-selected, [data-bw-priced="dim"][aria-checked="true"]')) return false;
    } catch (e2) {}
    return true;
  }

  function attrPricedReady(root, clickedEl) {
    var hasColour = false;
    var hasDim = false;
    try {
      hasColour = !!root.querySelector('[data-bw-priced="colour"]');
      var dims = root.querySelectorAll('[data-bw-priced="dim"]');
      for (var i = 0; i < dims.length; i++) {
        if (isPricedDimField(dims[i])) { hasDim = true; break; }
      }
    } catch (e) {
      return false;
    }
    if (!hasColour && !hasDim) return false;
    if (hasColour && !hasSelectedColour(root, clickedEl)) return false;
    if (hasDim && !pricedDimsComplete(root, clickedEl)) return false;
    // Finish-and-size builders (EGGER lengths) mark both as buttons. A finish
    // tap is not a price until a size button is chosen. Builders that also
    // have dim fields keep the field check above.
    if (!hasDim && dimButtonPending(root, clickedEl)) return false;
    // Panel builders keep .pb-l / .pb-w in the page. A phone colour tap must
    // not count as priced until a row has both sizes.
    try {
      if (root.querySelector('.pb-l, .pb-w') && !hasCompleteItemDims(root)) return false;
    } catch (e2) {}
    return true;
  }

  function isPricedDimensionField(el) {
    try {
      if (pricedRole(el) === 'dim') return true;
      return !!(el && el.closest && el.closest(
        '.pb-l, .pb-w, [data-bw-w], [data-bw-d], [data-ttb-w], [data-ttb-d], [data-ttb-dia], .ipsx-dim-w, .ipsx-dim-h, [data-height-select], [data-depth-select]'
      ));
    } catch (e) {
      return false;
    }
  }

  function isPricedColourTarget(el) {
    try {
      if (!el || !el.closest) return false;
      if (el.closest('.pb-swatch--more, [data-pb-expand-colours], [data-pb-showall], [data-ttb-showall], [data-ipsx-showall], .ipsx-swatchmore, [data-ipsx-more]')) return false;
      if (pricedRole(el) === 'colour') return true;
      return !!(el.closest(
        '.pb-swatch, .bw-wt__swatch, .ttb-swatch, .ipsx-swatchcard, [data-colour-handle], [data-colour-key], .pb-chip[data-thick]'
      ));
    } catch (e) {
      return false;
    }
  }

  function hasSelectedColour(root, clickedEl) {
    if (clickedEl && isPricedColourTarget(clickedEl)) return true;
    if (!root) return false;
    try {
      return !!(root.querySelector(
        '.pb-swatch.is-active, .bw-wt__swatch.is-active, .ttb-swatch.is-active, .ipsx-swatchcard.is-active, [data-colour-handle].is-active, [data-colour-key].is-active, .pb-chip.is-active[data-thick], [data-bw-priced="colour"].is-selected, [data-bw-priced="colour"].is-active, [data-bw-priced="colour"].is-on, [data-bw-priced="colour"][aria-checked="true"]'
      ));
    } catch (e) {
      return false;
    }
  }

  function hasCompleteItemDims(root) {
    if (!root) return false;
    try {
      var rows = root.querySelectorAll('tr');
      for (var i = 0; i < rows.length; i++) {
        var lengthEl = rows[i].querySelector('.pb-l, .ipsx-dim-w');
        var widthEl = rows[i].querySelector('.pb-w, .ipsx-dim-h');
        if (lengthEl && widthEl && fieldFilled(lengthEl) && fieldFilled(widthEl)) return true;
      }
      var widthFields = root.querySelectorAll('[data-bw-w]');
      for (var j = 0; j < widthFields.length; j++) {
        var card = widthFields[j].closest('.bw-wt__card--size') || root;
        var wEl = card.querySelector('[data-bw-w]');
        var dEl = card.querySelector('[data-bw-d]');
        if (wEl && dEl && fieldFilled(wEl) && fieldFilled(dEl)) return true;
      }
      var ttbW = root.querySelector('[data-ttb-w]');
      var ttbD = root.querySelector('[data-ttb-d]');
      if (ttbW && ttbD && fieldFilled(ttbW) && fieldFilled(ttbD)) return true;
      var ttbDia = root.querySelector('[data-ttb-dia]');
      if (ttbDia && fieldFilled(ttbDia)) return true;
      var heightSel = root.querySelector('[data-height-select]');
      var depthSel = root.querySelector('[data-depth-select]');
      if (heightSel && depthSel && fieldFilled(heightSel) && fieldFilled(depthSel)) return true;
      if (pricedDimsComplete(root)) return true;
    } catch (e) {}
    return false;
  }

  function tryFirePriced(ev) {
    try {
      if (pricedFired) return;
      var t = ev && ev.target;
      var root = builderRoot(t);
      if (!root) return;
      if (!attrPricedReady(root, t)) {
        if (!hasSelectedColour(root, t)) return;
        if (!hasCompleteItemDims(root)) return;
      }
      pricedFired = true;
      fireGtag('configuration_priced', buildParams({
        builder: root.getAttribute('data-bw-builder')
      }));
    } catch (e) {}
  }

  function onMaybePriced(ev) {
    try {
      if (pricedFired) return;
      if (!ev || !ev.isTrusted) return;
      var t = ev.target;
      var dimEvent = (ev.type === 'input' || ev.type === 'change') && isPricedDimensionField(t);
      var colourEvent = ev.type === 'click' && isPricedColourTarget(t);
      var dimClick = ev.type === 'click' && pricedRole(t) === 'dim';
      if (!dimEvent && !colourEvent && !dimClick) return;
      tryFirePriced(ev);
      if (!pricedFired) setTimeout(function () { tryFirePriced(ev); }, 0);
    } catch (e) {}
  }

  try {
    document.addEventListener('input', onMaybePriced, false);
    document.addEventListener('change', onMaybePriced, false);
    // Capture: the washroom builder replaces its stage inside the same click
    // that chooses a range, which detaches the button before a bubble listener.
    document.addEventListener('click', onMaybePriced, true);
  } catch (e) {}

  function getUrl(input) {
    try {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
    } catch (e) {}
    return '';
  }

  function getMethod(input, init) {
    try {
      if (init && init.method) return String(init.method).toUpperCase();
      if (input && typeof input.method === 'string') return String(input.method).toUpperCase();
    } catch (e) {}
    return 'GET';
  }

  function isCartAdd(input, init) {
    try {
      if (getMethod(input, init) !== 'POST') return false;
      return /\/cart\/add\.js(?:\?|$)/i.test(getUrl(input));
    } catch (e) {
      return false;
    }
  }

  function itemProps(item) {
    var props = (item && item.properties) || {};
    if (props && typeof props === 'object') return props;
    return {};
  }

  function itemTitle(item) {
    try {
      return String((item && (item.product_title || item.title)) || '').trim().toLowerCase();
    } catch (e) {
      return '';
    }
  }

  function titleIsHelper(title) {
    var t = String(title || '').trim().toLowerCase();
    if (!t) return false;
    for (var i = 0; i < HELPER_TITLES.length; i++) {
      var helper = HELPER_TITLES[i];
      if (t === helper || t.indexOf(helper + ' - ') === 0 || t.indexOf(helper + ' / ') === 0) return true;
    }
    return false;
  }

  function isHelperItem(item) {
    try {
      var props = itemProps(item);
      var role = String(props._shopisaw_role || '').toLowerCase();
      if (role.indexOf('helper') === 0) return true;
      var titled = titleIsHelper(itemTitle(item));
      if (!titled) return false;
      var hasShopisaw = Object.prototype.hasOwnProperty.call(props, '_is_shopisaw');
      return hasShopisaw || titled;
    } catch (e) {
      return false;
    }
  }

  function lineItemsFromPayload(data) {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.items)) return data.items;
    if (data.id || data.product_id || data.variant_id) return [data];
    return [];
  }

  function lineValuePence(item) {
    var qty = Number(item && item.quantity) || 1;
    if (item && item.line_price != null && isFinite(Number(item.line_price))) return Number(item.line_price);
    if (item && item.final_line_price != null && isFinite(Number(item.final_line_price))) return Number(item.final_line_price);
    var unit = Number((item && (item.final_price != null ? item.final_price : item.price)) || 0);
    if (!isFinite(unit)) unit = 0;
    return unit * qty;
  }

  function reportAddToCart(data) {
    var items = lineItemsFromPayload(data);
    var rawCount = items.length;
    var filtered = [];
    var helpers = 0;
    for (var i = 0; i < items.length; i++) {
      if (isHelperItem(items[i])) helpers += 1;
      else filtered.push(items[i]);
    }
    var valuePence = 0;
    for (var j = 0; j < filtered.length; j++) valuePence += lineValuePence(filtered[j]);
    added({
      value: valuePence / 100,
      items_count_raw: rawCount,
      items_count_filtered: filtered.length,
      helpers_excluded: helpers
    });
  }

  function observeCartAdd(responsePromise) {
    try {
      responsePromise.then(function (response) {
        try {
          if (!response || !response.ok) return;
          var clone = response.clone();
          clone.json().then(function (data) {
            try {
              reportAddToCart(data);
            } catch (e) {}
          }).catch(function () {});
        } catch (e) {}
      }).catch(function () {});
    } catch (e) {}
  }

  try {
    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var responsePromise = nativeFetch.apply(this, arguments);
        try {
          if (isCartAdd(input, init)) observeCartAdd(responsePromise);
        } catch (e) {}
        return responsePromise;
      };
    }
  } catch (e) {}
})();
